import axios, { AxiosError, AxiosResponse } from "axios";
import { AuthorizedAgent } from "../types/payment.types";

// ── Shared axios instance with auth interceptor ──────────────────────────
const api = axios.create({
  baseURL: process.env.REACT_APP_SPRINTCHECKOUT_BASE_URL,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('jwtToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401 || error.code === 'ERR_NETWORK') {
      localStorage.removeItem('jwtToken');
      window.location.replace("#/auth/sign-in");
      // Return a pending promise so React doesn't catch an unhandled rejection
      // while the page is redirecting.
      return new Promise(() => { });
    }
    return Promise.reject(error);
  }
);

// ── Payment Settings ─────────────────────────────────────────────────────

export async function getMerchantPaymentSettings(setPaymentSettings: (data: any) => void) {
  try {
    const response = await api.get('/payment_settings');
    setPaymentSettings(response.data);
    return response;
  } catch (error) {
    const err = error as AxiosError;
    if (err.response?.status === 404) {
      console.log("Merchant Not Found, creating new one...");
      return await createNewPaymentSettings(setPaymentSettings);
    }
    throw error;
  }
}

export async function updateMerchantPaymentSettings(paymentSettingToUpdate: any) {
  const response = await api.put('/payment_settings', paymentSettingToUpdate);
  return response;
}

async function createNewPaymentSettings(setPaymentSettings: (data: any) => void) {
  const response = await api.post('/payment_settings', {});
  setPaymentSettings(response.data);
  return response;
}

// ── API Keys ─────────────────────────────────────────────────────────────

export async function getAPIKeys() {
  return await api.get('/payment_settings/keys');
}

export async function generateAPIKey(setApiKeys: (data: any) => void) {
  const keys = await api.post('/payment_settings/keys', {});
  setApiKeys(keys.data);
}

export async function deleteAPIKey(key: string) {
  await api.delete(`/payment_settings/keys/${key}`);
}

// ── Webhooks ─────────────────────────────────────────────────────────────

export async function getWebhookApiKey() {
  const response = await api.post('/payment_settings/webhooks/key', {});
  return response.data;
}

// ── Authorized agents (MCP grants) ───────────────────────────────────────

export async function getAuthorizedAgents(): Promise<AxiosResponse<AuthorizedAgent[]>> {
  return await api.get('/payment_settings/agents');
}

/** Revokes one agent authorization and returns the refreshed list. Revocation is
 *  immediate: every /mcp call re-reads the grant and requires it to be ACTIVE, so
 *  an already-issued access token stops working as soon as this returns. */
export async function revokeAuthorizedAgent(grantId: string): Promise<AxiosResponse<AuthorizedAgent[]>> {
  return await api.delete(`/payment_settings/agents/${grantId}`);
}

// ── User ─────────────────────────────────────────────────────────────────

export async function getUserInfo() {
  const response = await api.get('/payment_settings/users/info');
  return response.data;
}

// ── Orders ───────────────────────────────────────────────────────────────

export async function getOrders(page = 0, size = 10) {
  const response = await api.get(`/orders?page=${page}&size=${size}`);
  return response.data;
}

// ── Payment Links / Sessions ─────────────────────────────────────────────

export async function getPaymentLinks(page = 0, size = 10) {
  const response = await api.get(`/payment_session/payment_links?page=${page}&size=${size}`);
  return response.data;
}

export async function createPaymentLinkSession(sessionData: any) {
  const response = await axios.post(
    process.env.REACT_APP_SPRINTCHECKOUT_HOST + '/api/checkout/v2/payment_session/payment_links',
    sessionData,
    { headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } }
  );
  return response.data;
}

// ── Exchange Rates (external APIs, no auth) ──────────────────────────────

export async function getDolarBlue() {
  try {
    const response = await axios.get('https://dolarapi.com/v1/dolares/blue');
    return response.data;
  } catch (error) {
    console.error('Error fetching Dolar Blue:', error);
    return null;
  }
}

export async function getDolarCripto() {
  try {
    const response = await axios.get('https://dolarapi.com/v1/dolares/cripto');
    return response.data;
  } catch (error) {
    console.error('Error fetching Dolar Cripto:', error);
    return null;
  }
}

// ── WooCommerce ──────────────────────────────────────────────────────────

export async function getWoocommerceSiteStatus() {
  const response = await api.get('/payment_settings/woocommerce/site/status');
  return response.data;
}

export async function deleteWoocommerceAuthorization() {
  await api.delete('/payment_settings/woocommerce/authorization');
}
